import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Fab, CircularProgress, Button, Slide } from "@material-ui/core";
import {
  ChevronLeft,
  CheckCircleOutlined,
  HighlightOff,
  Close,
  ReportProblemOutlined
} from "@material-ui/icons";
import { withAlert } from "react-alert";

const Container = styled.div`
  position: fixed;
  z-index: 9999;
  right: 16px;
  bottom: 16px;
  display: flex;
  align-items: ${({ open }) => (open ? "end" : "center")};
`;
const JobsButton = styled(Fab)`
  background-color: white;
  border-radius: 16px;
  width: 64px;
  height: 64px;
`;
const ArrowButton = styled(Button)`
  background-color: white;
  min-width: unset;
  padding: 0;
  width: 24px;
  height: 48px;
  margin-right: 4px;
  border-radius: 4px;
`;
const ArrowIcon = styled(ChevronLeft)`
  color: ${({ status }) => {
    if (status === "success") return "#5BC259";
    if (status === "error") return "#EE4848";
    if (status === "warning") return "#ffcc00";
    return "#1C1F74";
  }};
  width: 32px;
  transition: transform 0.2s linear;
  ${({ open }) =>
    open
      ? `
    transform: rotate(180deg);
  `
      : ""}
`;
const Loading = styled(CircularProgress)`
  color: #1c1f74;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const CompletedIcon = styled(CheckCircleOutlined)`
  color: #5bc259;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const ErrorIcon = styled(HighlightOff)`
  color: #ee4848;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const WarningIcon = styled(ReportProblemOutlined)`
  color: #ffcc00;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const Queue = styled.div`
  display: flex;
  flex-direction: column;
`;
const QueueItem = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 400px;
  padding: 15px;
  background-color: white;
  margin-right: 8px;
  margin-top: 4px;
  border-radius: 4px;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2),
    0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
`;
const JobMessage = styled.p`
  margin: 0;
  font-family: Roboto, sans-serif;
  font-size: 18px;
  font-weight: 500;
`;
const MoreDetail = styled.span`
  color: #ee4848;
  text-decoration: underline;

  :hover {
    cursor: pointer;
    color: #c41414;
  }
`;
const JobDetail = styled.p`
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #828282;
  margin: 0;
`;
const CloseIcon = styled(Close)`
  color: #828282;
  width: 20px;
  height: 20px;
  :hover {
    cursor: pointer;
  }
`;
const JobIcons = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  justify-content: ${({ canClose }) => (canClose ? "space-between" : "end")};
  align-items: center;
`;

const JobsQueue = ({ queue, markAsRead, alert }) => {
  const [open, setOpen] = useState(false);

  if (!queue.length) return null;

  let displayStatus;
  const isLoading = queue.some(queueItem => queueItem.status === "pending");
  let failed = false;
  if (!isLoading) {
    failed = queue.some(queueItem => queueItem.status === "error");
    if (failed) {
      displayStatus = "error";
    } else {
      const hasWarning = queue.some(queueItem => queueItem.error);
      displayStatus = hasWarning ? "warning" : "success";
    }
  } else {
    displayStatus = "pending";
  }

  let StatusIcon = null;
  if (displayStatus === "pending") StatusIcon = Loading;
  if (displayStatus === "success") StatusIcon = CompletedIcon;
  if (displayStatus === "error") StatusIcon = ErrorIcon;
  if (displayStatus === "warning") StatusIcon = WarningIcon;

  const showAlert = errorMessage => {
    alert.show(errorMessage, { title: "Ocorreu um erro" });
  };

  return (
    <Container open={open}>
      <ArrowButton onClick={() => setOpen(!open)} variant="contained">
        <ArrowIcon open={open} status={displayStatus} />
      </ArrowButton>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Queue>
          {queue.map(job => {
            let statusText = null;
            let ItemStatusIcon = null;
            if (job.status === "pending") {
              statusText = (
                <JobMessage>Carregando notas. Por favor, aguarde.</JobMessage>
              );
              ItemStatusIcon = Loading;
            }
            if (job.status === "success") {
              if (job.error) {
                statusText = (
                  <JobMessage>
                    Solicitação finalizada com
                    {" "}
                    <MoreDetail onClick={() => showAlert(job.error)}>
                      alguns erros
                    </MoreDetail>
                  </JobMessage>
                );
                ItemStatusIcon = WarningIcon;
              } else {
                statusText = <JobMessage>Notas salvas com sucesso!</JobMessage>;
                ItemStatusIcon = CompletedIcon;
              }
            }
            if (job.status === "error") {
              statusText = (
                <JobMessage>
                  Erro ao salvar notas.
                  {" "}
                  <MoreDetail onClick={() => showAlert(job.error)}>
                    Mais detalhes
                  </MoreDetail>
                </JobMessage>
              );
              ItemStatusIcon = ErrorIcon;
            }

            const canClose = job.status !== "pending";

            return (
              <QueueItem>
                <div>
                  <JobDetail>{job.title}</JobDetail>
                  {statusText}
                </div>
                <JobIcons canClose={canClose}>
                  {canClose && <CloseIcon onClick={() => markAsRead(job.id)} />}
                  <ItemStatusIcon size={24} />
                </JobIcons>
              </QueueItem>
            );
          })}
        </Queue>
      </Slide>
      <JobsButton onClick={() => setOpen(!open)}>
        <StatusIcon size={48} />
      </JobsButton>
    </Container>
  );
};

JobsQueue.propTypes = {
  queue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      error: PropTypes.string,
      name: PropTypes.string,
      title: PropTypes.string
    })
  ).isRequired,
  markAsRead: PropTypes.func.isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired
};

export default withAlert()(JobsQueue);
